// components/features/support/tasks/TaskForm.tsx
import React, { useEffect, useState, useMemo, useRef } from "react";
import { X, Upload, Loader2, Search, ChevronDown, AlertCircle } from "lucide-react";
import { Task, TaskPriority, TaskStatus, Agent, ClientUser } from "../../../../types/features/support/Task";

export interface TaskFormValues {
  title: string;
  description: string;
  priority: TaskPriority;
  status: TaskStatus;
  due_date: string | null;
  client_id: string | null;
  agents: string[];
  files: File[];
  existingAttachments: string[];
  note: string | null; // ✅
}

interface TaskFormProps {
  initialData?: Task | null;
  admins: Agent[];
  clients: ClientUser[];
  onSubmit: (values: TaskFormValues) => Promise<void>;
  onCancel?: () => void;
  loading?: boolean;
  clientsLoading?: boolean;
}

const priorities: { value: TaskPriority; label: string }[] = [
  { value: "basse", label: "Basse" },
  { value: "moyenne", label: "Moyenne" },
  { value: "haute", label: "Haute" },
  { value: "critique", label: "Critique" },
];

const statuses: { value: TaskStatus; label: string }[] = [
  { value: "nouveau", label: "Nouveau" },
  { value: "en_cours", label: "En cours" },
  { value: "en_attente", label: "En attente" },
  { value: "resolu", label: "Résolu" },
];

const getClientName = (c: any): string => {
  if (!c) return "";
  if (typeof c === "string") return c;
  if (c.full_name) return c.full_name;
  if (c.name) return c.name;
  if (c.fullName) return c.fullName;
  const first = c.first_name || c.firstName || "";
  const last = c.last_name || c.lastName || "";
  const combined = `${first} ${last}`.trim();
  if (combined) return combined;
  return c.username || c.email || c.mail || c.id || "";
};

const getClientEmail = (c: any): string => {
  if (!c) return "";
  return c.email || c.mail || "";
};

const getClientId = (c: any): string => {
  if (!c) return "";
  if (typeof c === "string") return c;
  return c.id || c.user_id || c.userId || c.uuid || "";
};

export const TaskForm: React.FC<TaskFormProps> = ({
  initialData,
  admins,
  clients,
  onSubmit,
  onCancel,
  loading = false,
  clientsLoading = false,
}) => {
  const isEdit = Boolean(initialData);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<TaskPriority>("moyenne");
  const [status, setStatus] = useState<TaskStatus>("nouveau");
  const [dueDate, setDueDate] = useState("");
  const [selectedClient, setSelectedClient] = useState<string>("");
  const [selectedAdmins, setSelectedAdmins] = useState<string[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<string[]>([]);
  const [note, setNote] = useState<string>(""); // ✅
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [clientSearch, setClientSearch] = useState("");
  const [isClientDropdownOpen, setIsClientDropdownOpen] = useState(false);
  const clientDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (initialData) {
      setTitle(initialData.title || "");
      setDescription(initialData.description || "");
      setPriority(initialData.priority || "moyenne");
      setStatus(initialData.status || "nouveau");
      setDueDate(initialData.due_date ? new Date(initialData.due_date).toISOString().slice(0, 16) : "");
      setSelectedClient(initialData.client_id || "");
      setSelectedAdmins(initialData.agents?.map((a) => a.id) || []);
      setExistingAttachments(initialData.attachments || []);
      setNote((initialData as any).note || ""); // ✅
    }
  }, [initialData]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(event.target as Node)) {
        setIsClientDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const normalizedClients = useMemo(() => {
    if (!Array.isArray(clients)) return [];
    return clients.map((c) => ({
      raw: c,
      id: getClientId(c),
      name: getClientName(c),
      email: getClientEmail(c),
    }));
  }, [clients]);

  const filteredClients = useMemo(() => {
    const q = clientSearch.trim().toLowerCase();
    if (!q) return normalizedClients;
    return normalizedClients.filter((c) => c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q));
  }, [normalizedClients, clientSearch]);

  const selectedClientObj = useMemo(
    () => normalizedClients.find((c) => c.id === selectedClient) || null,
    [normalizedClients, selectedClient]
  );

  const toggleAdmin = (id: string) => {
    setSelectedAdmins((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const newFiles = Array.from(e.target.files);
    setFiles((prev) => [...prev, ...newFiles]);
    e.target.value = "";
  };

  const removeFile = (index: number) => setFiles((prev) => prev.filter((_, i) => i !== index));

  const removeExistingAttachment = (url: string) => {
    setExistingAttachments((prev) => prev.filter((a) => a !== url));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);

    if (!title.trim()) {
      setErrorMsg("Le titre est obligatoire");
      return;
    }
    if (selectedAdmins.length === 0) {
      setErrorMsg("Sélectionnez au moins un admin");
      return;
    }

    await onSubmit({
      title: title.trim(),
      description: description.trim(),
      priority,
      status,
      due_date: dueDate ? new Date(dueDate).toISOString() : null,
      client_id: selectedClient || null,
      agents: selectedAdmins,
      files,
      existingAttachments,
      note: note.trim() ? note.trim() : null, // ✅
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {errorMsg && <div className="bg-primary/10 text-primary text-sm px-3 py-2 rounded-lg">{errorMsg}</div>}

      {/* Titre */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Titre <span className="text-primary">*</span>
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Ex: Vérifier le KYC du client X"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          required
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Consigne / Description <span className="text-primary">*</span>
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          placeholder="Détaille la consigne..."
          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          required
        />
      </div>

      {/* ✅ Note — un seul champ, modifiable à n'importe quel statut */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Note
          <span className="ml-2 text-xs font-normal text-gray-400">(optionnel)</span>
        </label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          placeholder="Ajoutez une note : avancement, blocage, résolution, remarque..."
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
        />
        <div className="flex justify-end">
          <span className="text-xs text-gray-400">{note.length} caractères</span>
        </div>
      </div>

      {/* Client concerné avec recherche */}
      <div ref={clientDropdownRef} className="relative">
        <label className="block text-sm font-medium text-gray-700 mb-1">Client concerné</label>

        <button
          type="button"
          onClick={() => {
            setIsClientDropdownOpen((v) => !v);
            setClientSearch("");
          }}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-white"
        >
          <span className={selectedClientObj ? "text-gray-900" : "text-gray-400"}>
            {selectedClientObj
              ? `${selectedClientObj.name}${selectedClientObj.email ? ` • ${selectedClientObj.email}` : ""}`
              : "— Aucun client —"}
          </span>
          <ChevronDown className="w-4 h-4 text-gray-400" />
        </button>

        {isClientDropdownOpen && (
          <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-hidden flex flex-col">
            <div className="p-2 border-b border-gray-100 sticky top-0 bg-white">
              <div className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  autoFocus
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  placeholder="Rechercher un client (nom, email)..."
                  className="w-full border border-gray-200 rounded-md pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                />
              </div>
            </div>

            <div className="overflow-y-auto">
              <button
                type="button"
                onClick={() => {
                  setSelectedClient("");
                  setIsClientDropdownOpen(false);
                }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                  !selectedClient ? "bg-primary/5 text-primary font-medium" : "text-gray-700"
                }`}
              >
                — Aucun client —
              </button>

              {clientsLoading && (
                <div className="flex items-center gap-2 px-3 py-3 text-sm text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Chargement des clients...
                </div>
              )}

              {!clientsLoading && normalizedClients.length === 0 && (
                <div className="flex items-start gap-2 px-3 py-3 text-sm text-amber-600 bg-amber-50">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium">Aucun client disponible</p>
                    <p className="text-xs text-amber-700 mt-0.5">
                      Vérifiez que la liste des clients est bien chargée et passée au formulaire.
                    </p>
                  </div>
                </div>
              )}

              {!clientsLoading && normalizedClients.length > 0 && filteredClients.length === 0 && (
                <p className="text-sm text-gray-400 px-3 py-2">Aucun résultat pour « {clientSearch} »</p>
              )}

              {!clientsLoading &&
                filteredClients.map((c) => (
                  <button
                    key={c.id || c.name}
                    type="button"
                    onClick={() => {
                      setSelectedClient(c.id);
                      setIsClientDropdownOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex flex-col ${
                      selectedClient === c.id ? "bg-primary/5 text-primary font-medium" : "text-gray-700"
                    }`}
                  >
                    <span>{c.name || "(sans nom)"}</span>
                    {c.email && <span className="text-xs text-gray-400">{c.email}</span>}
                  </button>
                ))}
            </div>
          </div>
        )}
      </div>

      {/* Priorité + Statut + Échéance */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Priorité</label>
          <select
            value={priority}
            onChange={(e) => setPriority(e.target.value as TaskPriority)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          >
            {priorities.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as TaskStatus)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          >
            {statuses.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Échéance</label>
          <input
            type="datetime-local"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          />
        </div>
      </div>

      {/* Admins */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Admins concernés <span className="text-primary">*</span> ({selectedAdmins.length})
        </label>
        <div className="max-h-44 overflow-y-auto border border-gray-200 rounded-lg p-2 space-y-1">
          {admins.length === 0 && <p className="text-sm text-gray-400 p-2">Aucun admin disponible</p>}
          {admins.map((a) => (
            <label key={a.id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer">
              <input
                type="checkbox"
                checked={selectedAdmins.includes(a.id)}
                onChange={() => toggleAdmin(a.id)}
                className="accent-primary"
              />
              <span className="text-sm text-gray-800">{a.name}</span>
              <span className="text-xs text-gray-400">{a.email}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Pièces jointes */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Pièces jointes</label>
        <label className="flex items-center gap-2 cursor-pointer border border-dashed border-gray-300 rounded-lg px-3 py-3 text-sm text-gray-500 hover:border-primary hover:text-primary">
          <Upload className="w-4 h-4" />
          Ajouter des images / captures
          <input type="file" accept="image/*,.pdf" multiple onChange={handleFilesChange} className="hidden" />
        </label>

        {existingAttachments.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {existingAttachments.map((url) => (
              <div key={url} className="relative">
                <img src={url} alt="attachment" className="w-16 h-16 object-cover rounded border border-gray-200" />
                <button
                  type="button"
                  onClick={() => removeExistingAttachment(url)}
                  className="absolute -top-1 -right-1 bg-primary text-white rounded-full w-5 h-5 text-xs flex items-center justify-center"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {files.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {files.map((file, index) => (
              <div key={index} className="relative">
                {file.type.startsWith("image/") ? (
                  <img
                    src={URL.createObjectURL(file)}
                    alt={file.name}
                    className="w-16 h-16 object-cover rounded border border-gray-200"
                  />
                ) : (
                  <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded text-xs text-gray-600 p-1 text-center">
                    {file.name}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => removeFile(index)}
                  className="absolute -top-1 -right-1 bg-primary text-white rounded-full w-5 h-5 text-xs flex items-center justify-center"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Annuler
          </button>
        )}
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 rounded-lg bg-primary text-white hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          {isEdit ? "Enregistrer" : "Créer la consigne"}
        </button>
      </div>
    </form>
  );
};
